import React, { useEffect, useState } from 'react';
import { Segment, Icon, Header, Button, Label, Message, Popup } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { sportOptions, createSportOptions } from '../../app/common/options/sportOptions';
import LoadingComponent from '../../app/layout/LoadingComponent';
import { useStore } from '../../app/stores/store';

const LEVEL_DESCRIPTIONS: Record<number, string> = {
    1: 'favoriteSports.level.beginner',
    2: 'favoriteSports.level.casual',
    3: 'favoriteSports.level.intermediate',
    4: 'favoriteSports.level.advanced',
    5: 'favoriteSports.level.competitive',
};

export default observer(function FavoriteSports() {
    const { profileStore } = useStore();
    const { profile, updateFavoriteSports, updateSportLevel, loading } = profileStore;
    const { t } = useTranslation();

    const [selectedSports, setSelectedSports] = useState<string[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const localizedSportOptions = createSportOptions(t, sportOptions).map(option => ({
        value: option.value,
        label: option.label
    }));

    useEffect(() => {
        if (profile?.sports) {
            setSelectedSports(profile.sports.map(s => s.id));
        }
    }, [profile]);

    const handleSportChange = (selectedOptions: any) => {
        const sportIds = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
        setSelectedSports(sportIds);
        const originalIds = profile?.sports?.map(s => s.id).sort() || [];
        setHasChanges(JSON.stringify(originalIds) !== JSON.stringify([...sportIds].sort()));
    };

    const handleSave = async () => {
        await updateFavoriteSports(selectedSports);
        setIsEditing(false);
        setHasChanges(false);
    };

    const handleCancel = () => {
        if (profile?.sports) setSelectedSports(profile.sports.map(s => s.id));
        else setSelectedSports([]);
        setIsEditing(false);
        setHasChanges(false);
    };

    if (!profile) return <LoadingComponent content={t('favoriteSports.loading')} />;

    return (
        <Segment>
            <Header as="h2" color="teal">
                <Icon name="star" />
                <Header.Content>
                    {t('favoriteSports.title')}
                    <Header.Subheader>{t('favoriteSports.subtitle')}</Header.Subheader>
                </Header.Content>
            </Header>

            {!isEditing ? (
                <>
                    <Message info>
                        <Message.Header>{t('favoriteSports.infoTitle')}</Message.Header>
                        <p>{t('favoriteSports.infoText')}</p>
                    </Message>

                    <div style={{ marginBottom: 20, marginTop: 20 }}>
                        {profile.sports && profile.sports.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {profile.sports.map((sport) => {
                                    const label = localizedSportOptions.find(
                                        opt => opt.value === sport.id
                                    )?.label ?? sport.sportName;

                                    return (
                                        <div key={sport.id} style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 8,
                                            padding: '12px 14px',
                                            background: '#f9fafb',
                                            borderRadius: 8,
                                            border: '1px solid #e8eaf6'
                                        }}>
                                            {/* Sport name */}
                                            <Label color="teal" style={{ alignSelf: 'flex-start' }}>
                                                <Icon name="check circle" />
                                                {label}
                                            </Label>

                                            {/* Level label + info */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6
                                            }}>
                                                <span style={{
                                                    fontSize: '0.78em',
                                                    color: '#6b7280',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.04em'
                                                }}>
                                                    {t('favoriteSports.yourLevelFor', { sport: label })}
                                                </span>
                                                <Popup
                                                    trigger={
                                                        <Icon
                                                            name='info circle'
                                                            style={{ color: '#9ca3af', cursor: 'pointer', fontSize: '0.9em' }}
                                                        />
                                                    }
                                                    size='small'
                                                    inverted
                                                    wide
                                                >
                                                    <div style={{ fontSize: '0.85em', lineHeight: 1.6 }}>
                                                        <div style={{ fontWeight: 700, marginBottom: 4 }}>
                                                            {t('favoriteSports.levelGuide')}
                                                        </div>
                                                        {[1, 2, 3, 4, 5].map(n => (
                                                            <div key={n}>
                                                                {'★'.repeat(n)}{'☆'.repeat(5 - n)} — {t(LEVEL_DESCRIPTIONS[n])}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Popup>
                                            </div>

                                            {/* Stars + value */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    {[1, 2, 3, 4, 5].map(star => (
                                                        <Icon
                                                            key={star}
                                                            name={
                                                                sport.selfReportedLevel != null &&
                                                                star <= sport.selfReportedLevel
                                                                    ? 'star'
                                                                    : 'star outline'
                                                            }
                                                            style={{
                                                                cursor: 'pointer',
                                                                color: star <= (sport.selfReportedLevel ?? 0)
                                                                    ? '#f59e0b'
                                                                    : '#d1d5db',
                                                                fontSize: '1.3em',
                                                                margin: 0,
                                                                transition: 'color 0.15s'
                                                            }}
                                                            onClick={() => {
                                                                const newLevel = sport.selfReportedLevel === star
                                                                    ? null
                                                                    : star;
                                                                updateSportLevel(sport.id, newLevel);
                                                            }}
                                                        />
                                                    ))}
                                                </div>

                                                <span style={{ fontSize: '0.82em', color: '#6b7280' }}>
                                                    {sport.selfReportedLevel
                                                        ? `${sport.selfReportedLevel}/5 — ${t(LEVEL_DESCRIPTIONS[sport.selfReportedLevel])}`
                                                        : t('favoriteSports.notAssessed')
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <Message warning>
                                <Icon name="warning sign" />
                                {t('favoriteSports.noSportsSelected')}
                            </Message>
                        )}
                    </div>

                    <Button color="teal" icon labelPosition="left" onClick={() => setIsEditing(true)}>
                        <Icon name="edit" />
                        {t('favoriteSports.editButton')}
                    </Button>
                </>
            ) : (
                <>
                    <Message>
                        <Message.Header>{t('favoriteSports.editModeTitle')}</Message.Header>
                        <p>{t('favoriteSports.editModeText')}</p>
                    </Message>

                    <div style={{ marginBottom: 20 }}>
                        <label style={{
                            display: 'block', marginBottom: 10,
                            fontWeight: 'bold', color: '#00b5ad'
                        }}>
                            {t('favoriteSports.selectLabel')}
                        </label>
                        <Select
                            isMulti
                            options={localizedSportOptions}
                            value={localizedSportOptions.filter(opt => selectedSports.includes(opt.value))}
                            onChange={handleSportChange}
                            placeholder={t('favoriteSports.selectPlaceholder')}
                            isSearchable
                            className="basic-multi-select"
                            classNamePrefix="select"
                            styles={{
                                control: (base: any) => ({
                                    ...base,
                                    minHeight: 42,
                                    borderColor: '#00b5ad',
                                    '&:hover': { borderColor: '#00a39a' }
                                }),
                                multiValue: (base: any) => ({ ...base, backgroundColor: '#e0f7f6' }),
                                multiValueLabel: (base: any) => ({
                                    ...base, color: '#00b5ad', fontWeight: '600'
                                }),
                                multiValueRemove: (base: any) => ({
                                    ...base,
                                    color: '#00b5ad',
                                    '&:hover': { backgroundColor: '#00b5ad', color: 'white' }
                                })
                            }}
                        />
                    </div>

                    {selectedSports.length > 0 && (
                        <Message positive>
                            <Message.Header>
                                {t('favoriteSports.selectedCount', { count: selectedSports.length })}
                            </Message.Header>
                        </Message>
                    )}

                    <div style={{ marginTop: 20 }}>
                        <Button positive icon labelPosition="left" onClick={handleSave}
                            disabled={loading || !hasChanges} loading={loading}>
                            <Icon name="save" />
                            {t('favoriteSports.saveButton')}
                        </Button>
                        <Button onClick={handleCancel} disabled={loading}>
                            <Icon name="cancel" />
                            {t('favoriteSports.cancelButton')}
                        </Button>
                    </div>
                </>
            )}
        </Segment>
    );
});